import type { QueryClient } from "@tanstack/react-query";
import { useMenu } from "@/features/menu/store";
import { useOnboarding } from "@/features/onboarding/store";
import { capacitorPreferencesStorage } from "@/lib/preferences-storage";
import { getPurchases } from "@/lib/purchases";

/**
 * Per-user scoping of the persisted client state (audit M28 / H5).
 *
 * The Zustand stores (`bc.menu`, `bc.onboarding`) are persisted to device
 * storage with no notion of *whose* data they hold. On a shared device,
 * user B would otherwise inherit user A's draft menu (and MenuPage would
 * auto-validate it under B's account) or A's onboarding answers.
 *
 * We record the Supabase user id that owns the persisted state under
 * {@link OWNER_KEY}. When a different user signs in, or on sign-out, every
 * user-scoped store and the TanStack Query cache are wiped.
 */
export const OWNER_KEY = "bc.owner";

/** Upper bound on how long we wait for a persisted store to rehydrate. */
const HYDRATION_TIMEOUT_MS = 3000;

interface PersistApi {
  persist: {
    hasHydrated: () => boolean;
    onFinishHydration: (fn: () => void) => () => void;
  };
}

/**
 * Resolve once the store has rehydrated from storage. Clearing a store
 * before hydration completes would be undone by the (async) rehydration
 * writing the stale persisted state back on top.
 */
export function waitForHydration(store: PersistApi): Promise<void> {
  if (store.persist.hasHydrated()) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      unsub();
      resolve();
    }, HYDRATION_TIMEOUT_MS);
    const unsub = store.persist.onFinishHydration(() => {
      clearTimeout(timer);
      unsub();
      resolve();
    });
  });
}

async function waitForStores(): Promise<void> {
  await Promise.all([
    waitForHydration(useMenu),
    waitForHydration(useOnboarding),
  ]);
}

function clearMenuStore(): void {
  const menu = useMenu.getState();
  menu.clearPlan();
  menu.clearNextWeekPlan();
}

async function readOwner(): Promise<string | null> {
  try {
    return (await capacitorPreferencesStorage.getItem(OWNER_KEY)) as
      | string
      | null;
  } catch {
    return null;
  }
}

/**
 * Make the persisted state belong to `userId`.
 *
 * - Same owner → no-op.
 * - Different known owner → wipe every user-scoped store + query cache.
 * - Unknown owner (first sign-in on this device, or state written before
 *   ownership tracking existed) → drop any menu draft (it cannot be proven
 *   to be this user's; a validated menu is re-fetched from the server) but
 *   keep the anonymous onboarding answers so `runAfterAuth` can sync them.
 */
export function claimPersistedState(
  userId: string,
  queryClient: QueryClient,
): Promise<void> {
  // Serialised: AuthProvider (initial session / SIGNED_IN) and runAfterAuth
  // may claim concurrently; running them one after the other guarantees the
  // second sees the owner the first recorded, and that nothing reads the
  // stores (e.g. the onboarding sync) before a foreign user's state is gone.
  const run = claimChain.then(() => doClaim(userId, queryClient));
  claimChain = run.catch(() => undefined);
  return run;
}

let claimChain: Promise<void> = Promise.resolve();

async function doClaim(
  userId: string,
  queryClient: QueryClient,
): Promise<void> {
  await waitForStores();
  const owner = await readOwner();
  if (owner === userId) return;

  clearMenuStore();
  if (owner !== null) {
    useOnboarding.getState().reset();
  }
  queryClient.clear();

  try {
    await capacitorPreferencesStorage.setItem(OWNER_KEY, userId);
  } catch {
    // Storage unavailable — worst case we wipe again on next launch.
  }
}

/**
 * Wipe everything tied to the signed-in user: persisted stores, the query
 * cache, the ownership marker and the RevenueCat identity.
 */
export function resetUserState(queryClient: QueryClient): Promise<void> {
  const run = claimChain.then(() => doReset(queryClient));
  claimChain = run.catch(() => undefined);
  return run;
}

async function doReset(queryClient: QueryClient): Promise<void> {
  await waitForStores();
  clearMenuStore();
  useOnboarding.getState().reset();
  queryClient.clear();
  try {
    await capacitorPreferencesStorage.removeItem(OWNER_KEY);
  } catch {
    // ignore
  }
  try {
    await getPurchases().logOut();
  } catch {
    // RevenueCat not configured / already anonymous — nothing to log out.
  }
}
